import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GetRecord from '../../../lib/api/GetRecord';
import { APIS } from '../../../lib/constants/apis';
import AddIcon from '@mui/icons-material/Add';
import POP_LEVEL from '../../../lib/api/Populations.json';
import { enumT, removeDataKeys, removeNullDeep } from '../../../utils/helper';
import { get } from 'lodash';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { DisplayDate } from '../../../utils/formats';

import LoadingBackdrop from '../../../components/ui-component/loading/LoadingBackdrop';
import { PostRecord } from '../../../lib/api/PostRecord';

import Schema from '../../../lib/paths/zuwendungsantrag_FOERDERUNG_UEBERSICHT_SEITE_Paths.json';
import MittelabrufSchema from '../../../lib/paths/mittelabruf_FULL_Paths.json';
import VNSchema from '../../../lib/paths/verwendungsnachweis_FULL_Paths.json';

const P = Schema.attributes;
const MA_P = MittelabrufSchema.attributes;
const VN_P = VNSchema.attributes;

function FoerderungUebersicht() {
	const navigate = useNavigate();
	const { id } = useParams();
	const numericId = id ? parseInt(id) : -1;
	const [loading, setLoading] = useState(false);
	const [applicationData, setApplicationData] = useState({});
	const MAX_LEVEL_MITTEL_ABRUF = 4;

	const mittelAbruf = get(applicationData, P.Zuwendungsantrag_Mittelabrufs.path, []);
	const verwendungsnachweis = get(applicationData, P.Zuwendungsantrag_Verwendungsnachweiss.path, []);

	const handleCreateMittelabruf = async () => {
		const MA_Data = {
			Allgemein_Abrufdatum: new Date(),
			Zuwendungsantrag: get(applicationData, 'id'),
			Mittelabrufstatus_Mittelabrufstatus: 'inBearbeitung',
			Bankverbindung_IBAN: get(applicationData, P.Antragstellerin_Bankverbindung_IBAN.path),
			Bankverbindung_Institut: get(applicationData, P.Antragstellerin_Bankverbindung_Geldinstitut.path),
			Berechnung_Der_Mittelanf_0071115044_ittel_Gem_Zuwendungsbescheid: get(
				applicationData,
				P.Zusammenfassung_Foerdersumme_Bewilligt.path,
			),
		};
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Mittelabruf, { data: MA_Data });
			if (res)
				navigate(`/meine-antraege/online-antrag/${get(res, 'data.id')}`, {
					state: { api: APIS.Mittelabruf },
				});
		} catch (err: any) {
			console.error('ERROR: Creating Mittelabruf ', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateVN = async () => {
		const VNDATA = {
			Allgemein_Verwendungsnachweis_Vom: new Date(),
			Verwendungsnachweisstatus_Status: 'inBearbeitung',
			Zuwendungsantrag: get(applicationData, 'id'),
		};

		try {
			setLoading(true);
			let res = await PostRecord(APIS.Verwendungsnachweis, { data: VNDATA });
			if (res)
				navigate(`/meine-antraege/online-antrag/${get(res, 'data.id')}`, {
					state: { api: APIS.Verwendungsnachweis },
				});
		} catch (err: any) {
			console.error('ERROR: Creating Verwendungsnachweis ', err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			let res = await GetRecord(APIS.Zuwendungsantrag, numericId, POP_LEVEL.zuwendungsantrag.FOERDERUNG_UEBERSICHT_SEITE);
			setApplicationData(removeNullDeep(removeDataKeys(get(res, 'data', []))));
		};
		fetch();
	}, [numericId]);

	return (
		<>
			<Container>
				<LoadingBackdrop open={loading} />
				<Stack spacing={3}>
					<Box>
						<Typography variant="h3" component={'h1'}>
							Förderdashboard: {get(applicationData, P.Foerderprogramm.schema.attributes.Allgemein_Name.path)}
						</Typography>

						<Typography variant="h4" component={'h2'}>
							Antrag: {get(applicationData, P.Allgemein_Projekttitel.path, '')}
						</Typography>
					</Box>
					<Typography variant="body1">Dies ist ein Beispieltext, der als Platzhalter dient.</Typography>
					<Stack direction={'row'} justifyContent={'space-between'}>
						<AntragsButtonWrapper title="Antrag:">
							<AntragButton
								name={`Antrag ${get(applicationData, P.Antragsstatus_Antragsstatus.path) === 'inBearbeitung' || get(applicationData, P.Antragsstatus_Antragsstatus.path) === 'inUeberarbeitungDurchAntragssteller' ? 'bearbeiten' : 'ansehen'}`}
								description={'Status: ' + enumT(get(applicationData, P.Antragsstatus_Antragsstatus.path))}
								onClick={() =>
									navigate(`/meine-antraege/online-antrag/${numericId}`, {
										state: { api: APIS.Zuwendungsantrag },
									})
								}
							/>
						</AntragsButtonWrapper>
					</Stack>
					<AntragsButtonWrapper title="Mittelabruf:">
						{mittelAbruf.length > 0 ? (
							<>
								{mittelAbruf.map((eachMittelabruf: any, i: number) => {
									return (
										<AntragButton
											key={i}
											name={'Abrufdatum: ' + DisplayDate(get(eachMittelabruf, MA_P.Allgemein_Abrufdatum.path, ''))}
											description={
												'Status: ' + enumT(get(eachMittelabruf, MA_P.Mittelabrufstatus_Mittelabrufstatus.path))
											}
											onClick={() =>
												navigate(`/meine-antraege/online-antrag/${get(eachMittelabruf, 'id')}`, {
													state: { api: APIS.Mittelabruf },
												})
											}
										/>
									);
								})}
								{Array.from({ length: MAX_LEVEL_MITTEL_ABRUF - mittelAbruf.length }).map(() => {
									return (
										<AntragButton
											name="Mittel abrufen"
											description="erstellen"
											onClick={() => handleCreateMittelabruf()}
											variant={'contained'}
										/>
									);
								})}
							</>
						) : (
							<>
								{get(applicationData, P.Antragsstatus_Antragsstatus.path, '') === 'inFoerderung' ? (
									<>
										<AntragButton
											name="Mittel abrufen"
											description="erstellen"
											onClick={() => handleCreateMittelabruf()}
											variant={'contained'}
										/>
									</>
								) : (
									<>
										<AntragButton
											name="Noch nicht möglich"
											description="offen"
											onClick={() => navigate('#')}
											disabled={true}
										/>
									</>
								)}
							</>
						)}
					</AntragsButtonWrapper>

					<AntragsButtonWrapper title="Verwendungsnachweis:">
						{verwendungsnachweis.length > 0 ? (
							<>
								{verwendungsnachweis.map((eachVN: any, i: number) => {
									return (
										<AntragButton
											key={i}
											name={'Vom: ' + DisplayDate(get(eachVN, VN_P.Allgemein_Verwendungsnachweis_Vom.path, ''))}
											description={'Status: ' + enumT(get(eachVN, VN_P.Verwendungsnachweisstatus_Status.path))}
											onClick={() =>
												navigate(`/meine-antraege/online-antrag/${get(eachVN, 'id')}`, {
													state: { api: APIS.Verwendungsnachweis },
												})
											}
										/>
									);
								})}
							</>
						) : (
							<>
								{get(applicationData, P.Antragsstatus_Antragsstatus.path, '') === 'inFoerderung' ? (
									<>
										<AntragButton
											name="Verwendungsnachweis"
											description="erstellen"
											onClick={() => handleCreateVN()}
											variant={'contained'}
										/>
									</>
								) : (
									<>
										<AntragButton
											name="Noch nicht möglich"
											description="offen"
											onClick={() => navigate('#')}
											disabled={true}
										/>
									</>
								)}
							</>
						)}
					</AntragsButtonWrapper>
				</Stack>
			</Container>
		</>
	);
}
export default FoerderungUebersicht;

type AntragButtonProps = {
	name: string;
	description: string;
	onClick: () => void;
	variant?: 'text' | 'outlined' | 'contained';
	disabled?: boolean;
};
export const AntragButton: FC<AntragButtonProps> = ({ name, description, onClick, disabled, variant = 'outlined' }) => {
	return (
		<>
			<Button
				disabled={disabled}
				variant={variant}
				onClick={() => onClick()}
				sx={{
					borderRadius: 0,
					borderColor: '#e4e4e4',
					textAlign: 'left',
					width: '26.5rem',
					justifyContent: 'space-between',
					padding: '1rem 1rem',
					marginTop: '0.5rem !important',
					maxWidth: 'unset',
				}}
				endIcon={variant === 'contained' ? <AddIcon sx={{ fontSize: '2.5rem !important' }} /> : null}
			>
				<Stack>
					<Typography variant="h5">{name}</Typography>
					<Typography variant="body2">{description}</Typography>
				</Stack>
			</Button>
		</>
	);
};

type AntragsButtonWrapperType = {
	title: string;
	children: ReactNode;
};
export const AntragsButtonWrapper: FC<AntragsButtonWrapperType> = ({ title, children }) => {
	return (
		<Stack spacing={0}>
			<Typography variant="h6">{title}</Typography>
			<Stack direction={'column'} columnGap={5}>
				{children}
			</Stack>
		</Stack>
	);
};
